/**
 * Frame Animation Hook
 * Manages animation state and provides callbacks for frame animation
 */

import { useState, useCallback } from 'react'
import { AnimationTrigger, getAnimationConfig } from '../config/animationConfig'
import { FrameAnimationService } from '../services/frameAnimationService'
import { useAppStore } from '../store/appStore'

export interface UseFrameAnimationReturn {
  /** Current frame being animated (null when not animating) */
  animatingFrameIndex: number | null
  /** Current animation trigger type (preview, confirm, or null) */
  animationTrigger: AnimationTrigger
  /** Callback to pass to animation service for frame updates */
  handleFrameShow: (frameIndex: number) => void
  /** Reset animation state */
  resetAnimation: () => void
  /** Start new animation sequence */
  startAnimation: (
    frames: number[],
    targetFrame: number,
    trigger: AnimationTrigger,
    onComplete: (targetFrame: number) => void,
  ) => () => void
}

/**
 * Hook to manage frame animation state and lifecycle
 * @param animationService - Service to handle animation logic
 * @returns Object with animation state and callbacks
 */
export function useFrameAnimation(animationService: FrameAnimationService): UseFrameAnimationReturn {
  const [animatingFrameIndex, setAnimatingFrameIndex] = useState<number | null>(null)
  const [animationTrigger, setAnimationTrigger] = useState<AnimationTrigger>(null)

  const handleFrameShow = useCallback((frameIndex: number) => {
    setAnimatingFrameIndex(frameIndex)
    
    // 🎯 Center viewport on the animation frame (holy red dot centering)
    // This makes the animated frame appear centered on screen
    const state = useAppStore.getState()
    console.log(`🎯 [Animation] Centering viewport on frame ${frameIndex}`)
    state.centerViewportOnFrame(frameIndex)
  }, [])

  const resetAnimation = useCallback(() => {
    setAnimatingFrameIndex(null)
    setAnimationTrigger(null)
    animationService.cancel()
  }, [animationService])

  const startAnimation = useCallback(
    (frames: number[], targetFrame: number, trigger: AnimationTrigger, onComplete: (targetFrame: number) => void) => {
      if (!trigger) {
        console.warn('useFrameAnimation: startAnimation called with null trigger')
        return () => {}
      }

      setAnimationTrigger(trigger)
      const config = getAnimationConfig(trigger)

      if (!config) {
        console.warn(`useFrameAnimation: No config found for trigger ${trigger}`)
        return () => {}
      }

      const cancelFn = animationService.animateFrameSequence(
        frames,
        targetFrame,
        config,
        {
          onFrameShow: handleFrameShow,
          onComplete: (target) => {
            setAnimatingFrameIndex(null)
            setAnimationTrigger(null)
            onComplete(target)
          },
        },
      )

      return cancelFn
    },
    [animationService, handleFrameShow],
  )

  return {
    animatingFrameIndex,
    animationTrigger,
    handleFrameShow,
    resetAnimation,
    startAnimation,
  }
}
